// src/plugins/sales-forecast/components/AmenityOutput.tsx
import React, { useMemo } from "react";
import {
  useConfig,
  useElementData,
  useElementColumns,
} from "@sigmacomputing/plugin";
import { Card, CardHeader, CardContent } from "../../../components/ui/card";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import type { Params } from "./ControlPanel";
import type { EhRow } from "../lib/useSigmaElementData";
import "../../sales-forecast/styles/theme.css";
import neighborhoodGroups from "../../../server/neighborhood_groups.json";

// ---------------- Props ----------------
export interface AmenityOutputProps {
  onChange: (selected: string[]) => void;
  clusterName: string;
  clusterDescription: string;
  cluster: number; // ← ADDED: used for prototype Transitional/Signature ratio logic
  composition?:
    | {
        spec_pct?: number | string | null;
        presold_pct?: number | string | null;
        specPct?: number | string | null;
        presoldPct?: number | string | null;
        comp_fraction?: number | string | null;
        compFraction?: number | string | null;
      }
    | string
    | null;
  serverSpecPct?: number | string | null;
  serverPresoldPct?: number | string | null;
  forecastData?: {
    date: string;
    presolds: number;
    sold_specs: number;
    unsold_specs: number;
    available_lots: number;
  }[];
  errorText?: string;
  params: Params;
  elementRows: EhRow[];
}

// ---------------- Utility helpers ----------------
function normalizeComposition(input: AmenityOutputProps["composition"]) {
  if (input == null) return null;
  if (typeof input === "string") {
    try {
      const o = JSON.parse(input);
      return o && typeof o === "object" ? o : null;
    } catch {
      return null;
    }
  }
  return input;
}

function avgOrNull(a?: number | null, b?: number | null): number | null {
  if (a == null && b == null) return null;
  if (a == null) return b ?? null;
  if (b == null) return a ?? null;
  return (a + b) / 2;
}

function sumOrNull(
  a: number | null | undefined,
  b: number | null | undefined
): number | null {
  if (a == null || b == null) return null;
  return a + b;
}

function formatCurrencyOrDash(v: number | null) {
  return v != null
    ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "—";
}

function coerceNumberLike(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const cleaned = v.trim().replace(/%$/, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toPercentOrNull(v: unknown): number | null {
  const n = coerceNumberLike(v);
  if (n == null) return null;
  const pct = n >= 0 && n <= 1 ? n * 100 : n;
  if (!Number.isFinite(pct)) return null;
  return Math.round(Math.min(100, Math.max(0, pct)));
}

function mean(values: (number | null | undefined)[]): number | null {
  const valid = values.filter(
    (v): v is number => typeof v === "number" && Number.isFinite(v) && v > 0
  );
  return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
}

function mode(values: (string | null | undefined)[]): string | null {
  const counts: Record<string, number> = {};
  for (const v of values) {
    if (!v) continue;
    counts[v] = (counts[v] || 0) + 1;
  }
  const entries = Object.entries(counts);
  if (!entries.length) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

function formatLongDate(date: Date): string {
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
    .format(date)
    .replace(/\d+/, `${day}${suffix}`);
}

function toIsoOrNull(v: unknown): string | null {
  if (!v) return null;
  try {
    const d = v instanceof Date ? v : new Date(v as any);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

function clampPositive(n: number) {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function safePerMonth(total: number, months: number): number | null {
  if (!Number.isFinite(total) || !Number.isFinite(months) || months <= 0)
    return null;
  return total / months;
}

function approxMonthsBetween(a: Date, b: Date): number {
  const ms = b.getTime() - a.getTime();
  const days = ms / (1000 * 60 * 60 * 24);
  return Math.max(0, days / 30.4375);
}

function normKey(s: unknown): string {
  return String(s ?? "").toLowerCase().trim();
}

function roundInt(n: unknown): number {
  const x = Number(n);
  return Number.isFinite(x) ? Math.max(0, Math.round(x)) : 0;
}

// ---------------- Component ----------------
export const AmenityOutput: React.FC<AmenityOutputProps> = (props) => {
  // ← ADDED cluster to destructure
  const { composition, forecastData, errorText, params, elementRows, cluster } = props;
  const theme = useSystemTheme();

  // 🔹 Sigma Data
  const cfg = useConfig() as any;
  const elementId: string = (cfg?.ehMaster ?? cfg?.dataSource ?? "") as string;
  const rawDataAny = useElementData(elementId) as Record<string, any[]> | undefined;
  useElementColumns(elementId);

  // If elementRows is present, prefer it. Otherwise build rows directly from rawDataAny.
  const directRows: EhRow[] = useMemo(() => {
    if (!rawDataAny || typeof rawDataAny !== "object") return [];

    const pick = (id: string | null, i: number) =>
      id && Array.isArray(rawDataAny[id]) ? rawDataAny[id][i] : null;

    const idAddress = cfg?.addressCol ?? null;
    const idCity = cfg?.cityCol ?? null;
    const idNeigh = cfg?.neighborhoodCol ?? null;
    const idHouseType = cfg?.houseTypeCol ?? null;
    const idContractDate = cfg?.contractDateCol ?? null;
    const idGreenLight = cfg?.officeGreenLightCol ?? null;
    const idActiveComps = cfg?.activeCompsCol ?? null;
    const idMarket = cfg?.marketCol ?? null;
    const idSchoolDistrict = cfg?.schoolDistrictCol ?? null;
    const idFloorPlan = cfg?.floorPlanCol ?? null;
    const idStyle = cfg?.styleCol ?? null;
    const idPrice = cfg?.priceCol ?? null;
    const idSQFT = cfg?.sqftCol ?? null;
    const idPropertyType = cfg?.propertyTypeCol ?? null;

    const idForLen =
      idAddress ||
      idCity ||
      idNeigh ||
      idHouseType ||
      idContractDate ||
      idGreenLight ||
      idActiveComps ||
      idMarket ||
      idSchoolDistrict ||
      idPropertyType;

    if (!idForLen || !Array.isArray(rawDataAny[idForLen])) return [];

    const len = rawDataAny[idForLen].length;
    const out: EhRow[] = new Array(len);

    for (let i = 0; i < len; i++) {
      const dt = pick(idContractDate, i);
      const dt2 = pick(idGreenLight, i);
      const ac = pick(idActiveComps, i);
      const pc = pick(idPrice, i);
      const sq = pick(idSQFT, i);

      out[i] = {
        address: pick(idAddress, i),
        city: pick(idCity, i),
        neighborhood: pick(idNeigh, i),
        houseType: pick(idHouseType, i),
        contractDate: dt ? toIsoOrNull(dt) : null,
        officeGreenLight: dt2 ? toIsoOrNull(dt2) : null,
        activeComps: ac === null || ac === undefined ? null : Number(ac),
        market: pick(idMarket, i),
        schoolDistrict: pick(idSchoolDistrict, i),
        floorPlan: pick(idFloorPlan, i),
        style: pick(idStyle, i),
        price: pc === null || pc === undefined ? null : Number(pc),
        sqft: sq === null || sq === undefined ? null : Number(sq),
        propertyType: idPropertyType
          ? pick(idPropertyType, i) != null
            ? String(pick(idPropertyType, i))
            : null
          : null,
      } as EhRow;
    }

    return out;
  }, [rawDataAny, cfg]);

  const rows: EhRow[] = useMemo(() => {
    return Array.isArray(elementRows) && elementRows.length ? elementRows : directRows;
  }, [elementRows, directRows]);

  // ---------- Composition ----------
  const comp = normalizeComposition(composition);
  let spec = toPercentOrNull(comp?.spec_pct ?? comp?.specPct);
  let presold = toPercentOrNull(comp?.presold_pct ?? comp?.presoldPct);

  if (
    (spec == null || presold == null) &&
    (comp?.comp_fraction != null || comp?.compFraction != null)
  ) {
    const fromFrac = toPercentOrNull(comp?.comp_fraction ?? comp?.compFraction);
    if (fromFrac != null) {
      if (spec == null) spec = fromFrac;
      if (presold == null) presold = 100 - fromFrac;
    }
  }

  // ---------- Summary Statistics + Peak Demand ----------
  const summary = useMemo(() => {
    if (!forecastData || forecastData.length === 0) return null;

    const data = forecastData
      .map((d) => ({ ...d, dateObj: new Date(d.date) }))
      .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());

    const startDate = data[0].dateObj;
    const endDate = data[data.length - 1].dateObj;

    const diffMs = endDate.getTime() - startDate.getTime();
    const totalDays = diffMs / (1000 * 60 * 60 * 24);
    const totalYears = totalDays / 365;

    const today = new Date();

    let current = data.find((d, i) => {
      const next = data[i + 1];
      return d.dateObj <= today && (!next || next.dateObj > today);
    });

    if (!current) {
      current = data.reduce((a, b) =>
        Math.abs(a.dateObj.getTime() - today.getTime()) <
        Math.abs(b.dateObj.getTime() - today.getTime())
          ? a
          : b
      );
    }

    const first = data[0];
    const last = data[data.length - 1];

    const computePeakQuarter = (
      slice: typeof data
    ): { key: string; specs: number; presolds: number; total: number } | null => {
      if (!slice || slice.length < 2) return null;

      const quarterSales: Record<
        string,
        { specs: number; presolds: number; total: number }
      > = {};

      for (let i = 1; i < slice.length; i++) {
        const prev = slice[i - 1];
        const curr = slice[i];

        const addSpecs = clampPositive(curr.sold_specs - prev.sold_specs);
        const addPresolds = clampPositive(curr.presolds - prev.presolds);

        const currDate = curr.dateObj;
        const q = Math.floor(currDate.getMonth() / 3) + 1;
        const y = currDate.getFullYear();
        const key = `${y}-Q${q}`;

        if (!quarterSales[key]) quarterSales[key] = { specs: 0, presolds: 0, total: 0 };
        quarterSales[key].specs += addSpecs;
        quarterSales[key].presolds += addPresolds;
        quarterSales[key].total += addSpecs + addPresolds;
      }

      let peak: { key: string; specs: number; presolds: number; total: number } | null =
        null;
      for (const [key, val] of Object.entries(quarterSales)) {
        if (!peak || val.total > peak.total) peak = { key, ...val };
      }
      return peak;
    };

    const recorded = data.filter((d) => d.dateObj <= today);
    const forecasted = data.filter((d) => d.dateObj > today);

    return {
      startDate,
      endDate,
      totalYears,
      first,
      current,
      last,
      recordedPeakQuarter: computePeakQuarter(recorded),
      forecastedPeakQuarter: computePeakQuarter(forecasted),
    };
  }, [forecastData]);

  // ---------- House Sales Statistics ----------
  const { filteredRowsForNeighborhood, houseStats } = useMemo(() => {
    if (!rows || !rows.length)
      return {
        filteredRowsForNeighborhood: [] as EhRow[],
        houseStats: null as any,
      };

    const target = (params.neighborhoodTitle || "").toLowerCase().trim();

    // Normalize cloneType to handle hyphenated variants like "Neighborhood - Group"
    // vs "neighborhood group". Collapses " - " (or any "-" with surrounding whitespace)
    // into a single space so the comparison matches our canonical lowercase keys.
    const rawCloneType = (params.cloneType || "")
      .toLowerCase()
      .trim()
      .replace(/\s*-\s*/g, " ");

    // Supported cloneTypes:
    //   "neighborhood"       → single neighborhood only
    //   "neighborhood group" → all neighborhoods in the same group
    //   "school district"    → all rows matching school district
    //   "market"             → all rows matching market
    const cloneType =
      rawCloneType === "market"
        ? "market"
        : rawCloneType === "school district"
        ? "school district"
        : rawCloneType === "neighborhood group"
        ? "neighborhood group"
        : "neighborhood";

    // Build the neighborhood group lookup regardless of cloneType —
    // needed for both "neighborhood" and "neighborhood group" branches.
    const normalizedGroups = Object.entries(neighborhoodGroups).map(([key, list]) => [
      key.toLowerCase().trim(),
      (list as string[]).map((n) => n.toLowerCase().trim()),
    ]);

    // All neighborhoods in the same group as target (used by both neighborhood branches)
    const fromChild = normalizedGroups.find(([_, list]) =>
      (list as string[]).includes(target)
    );
    const fromGroup = normalizedGroups.find(([key]) => key === target);
    const found = (fromChild || fromGroup || [target, [target]]) as [string, string[]];
    const groupNeighborhoods = Array.from(new Set<string>([found[0], ...(found[1] || [])]));

    // scopeFilter: the full peer set for this cloneType — used for
    // price/sqft averages and Transitional/Signature ratio calculations.
    const scopeFilter = (r: EhRow): boolean => {
      if (cloneType === "market") {
        return (r.market ?? "").toString().toLowerCase().trim() === target;
      }
      if (cloneType === "school district") {
        return (r.schoolDistrict ?? "").toString().toLowerCase().trim() === target;
      }
      if (cloneType === "neighborhood group") {
        return groupNeighborhoods.includes((r.neighborhood ?? "").toString().toLowerCase().trim());
      }
      // "neighborhood" — single neighborhood only
      return (r.neighborhood ?? "").toString().toLowerCase().trim() === target;
    };

    // filtered: the rows for THIS specific neighborhood/entity (used for
    // lot type ratios and the filteredRowsForNeighborhood return value).
    let filtered = rows.filter((r) => {
      if (cloneType === "market") {
        return (r.market ?? "").toString().toLowerCase().trim() === target;
      }
      if (cloneType === "school district") {
        return (r.schoolDistrict ?? "").toString().toLowerCase().trim() === target;
      }
      if (cloneType === "neighborhood group") {
        return groupNeighborhoods.includes((r.neighborhood ?? "").toString().toLowerCase().trim());
      }
      // "neighborhood"
      return (r.neighborhood ?? "").toString().toLowerCase().trim() === target;
    });

    if (!filtered.length) filtered = rows;

    // ── Price / sqft source rows ──────────────────────────────────────────────
    // Prototype: use last 90 days only (no real history exists).
    // Real neighborhoods: use all recorded sales from the full scope
    // (neighborhood group, school district, or market) for richer averages.
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 90);

    const isWithin90Days = (r: EhRow): boolean => {
      if (!r.contractDate) return false;
      const d = new Date(r.contractDate);
      return !isNaN(d.getTime()) && d >= cutoff && d <= now;
    };

    // For real neighborhoods, pull price/sqft from the full scope (same peer
    // set used for Transitional/Signature ratios) so presold averages are
    // populated even when recent 90-day data is sparse.
    const isPrototypeForPrice =
      target.includes("prototype") ||
      (params.neighborhoodID ?? "").toString().toLowerCase().includes("prototype");

    let priceSourceSpecs: EhRow[];
    let priceSourcePresolds: EhRow[];

    if (isPrototypeForPrice) {
      // Prototype: 90-day filtered rows from the local filtered set
      priceSourceSpecs = filtered.filter(
        (r) =>
          (r.houseType === "Pending" || r.houseType === "Spec") &&
          r.contractDate != null &&
          isWithin90Days(r)
      );
      priceSourcePresolds = filtered.filter(
        (r) =>
          r.houseType === "Pre-Sold" &&
          r.contractDate != null &&
          isWithin90Days(r)
      );
    } else {
      // Real neighborhood: all recorded sales from the full scope,
      // scoped correctly to the cloneType (neighborhood, neighborhood group,
      // school district, or market) via the shared scopeFilter.
      const scopeForPrice = rows.filter(scopeFilter);

      priceSourceSpecs = scopeForPrice.filter(
        (r) =>
          (r.houseType === "Pending" || r.houseType === "Spec") &&
          r.contractDate != null
      );
      priceSourcePresolds = scopeForPrice.filter(
        (r) =>
          r.houseType === "Pre-Sold" &&
          r.contractDate != null
      );
    }

    // Keep 90-day aliases for prototype compatibility (used in hs below)
    const soldSpecs90    = priceSourceSpecs;
    const soldPresolds90 = priceSourcePresolds;

    // ── Transitional / Signature counts ───────────────────────────────────────
    // Three pathways based on data availability:
    //
    // 1. Prototype neighborhoods (no real history exists) — use global per-
    //    neighborhood style ratios scaled to availableLots.
    //
    // 2. Real neighborhoods with NO recorded sales — same approach as
    //    prototypes: fall back to global per-neighborhood style ratios so
    //    buckets reflect a realistic expected composition rather than zeros.
    //
    // 3. Real neighborhoods WITH recorded sales — use raw counts; if recorded
    //    rows are partial (< cap), proportionally fill the remainder using
    //    the same recorded style ratios.

    const cap = Math.max(0, roundInt((params as any)?.availableLots));

    // Helper: distribute `targetCap` lots across 4 style buckets using
    // global per-neighborhood ratios averaged across all neighborhoods that
    // have at least one sale in each category. This way no single large
    // neighborhood skews the result.
    const distributeByGlobalRatios = (targetCap: number) => {
      const neighborhoodStyleCounts: Record<
        string,
        {
          specT: number;
          specS: number;
          specTotal: number;
          presoldT: number;
          presoldS: number;
          presoldTotal: number;
        }
      > = {};

      for (const r of rows) {
        if (!r.contractDate) continue;
        const neigh = (r.neighborhood ?? "unknown").toLowerCase().trim();
        if (!neighborhoodStyleCounts[neigh]) {
          neighborhoodStyleCounts[neigh] = {
            specT: 0,
            specS: 0,
            specTotal: 0,
            presoldT: 0,
            presoldS: 0,
            presoldTotal: 0,
          };
        }
        const entry = neighborhoodStyleCounts[neigh];
        const isSpec = r.houseType === "Pending" || r.houseType === "Spec";
        const isPresold = r.houseType === "Pre-Sold";
        if (isSpec) {
          entry.specTotal++;
          if (r.style === "Transitional") entry.specT++;
          if (r.style === "Signature") entry.specS++;
        }
        if (isPresold) {
          entry.presoldTotal++;
          if (r.style === "Transitional") entry.presoldT++;
          if (r.style === "Signature") entry.presoldS++;
        }
      }

      const neighEntries = Object.values(neighborhoodStyleCounts);
      type StyleEntry = typeof neighEntries[0];
      const avgRatio = (
        extractor: (e: StyleEntry) => { num: number; denom: number }
      ): number => {
        const ratios = neighEntries
          .map(extractor)
          .filter((x) => x.denom > 0)
          .map((x) => x.num / x.denom);
        if (!ratios.length) return 0;
        return ratios.reduce((a, b) => a + b, 0) / ratios.length;
      };

      const specTRatio    = avgRatio((e) => ({ num: e.specT,    denom: e.specTotal    }));
      const specSRatio    = avgRatio((e) => ({ num: e.specS,    denom: e.specTotal    }));
      const presoldTRatio = avgRatio((e) => ({ num: e.presoldT, denom: e.presoldTotal }));
      const presoldSRatio = avgRatio((e) => ({ num: e.presoldS, denom: e.presoldTotal }));

      // Distribute targetCap proportionally across the four buckets so the
      // result always sums to targetCap (with the last bucket absorbing
      // any rounding drift).
      const totalRatio = specTRatio + specSRatio + presoldTRatio + presoldSRatio;

      if (totalRatio <= 0 || targetCap <= 0) {
        return { specT: 0, specS: 0, presoldT: 0, presoldS: 0 };
      }

      const specT    = Math.round((specTRatio    / totalRatio) * targetCap);
      const specS    = Math.round((specSRatio    / totalRatio) * targetCap);
      const presoldT = Math.round((presoldTRatio / totalRatio) * targetCap);
      const presoldS = Math.max(0, targetCap - specT - specS - presoldT);
      return { specT, specS, presoldT, presoldS };
    };

    const isPrototypeTarget =
      target.includes("prototype") ||
      (params.neighborhoodID ?? "").toString().toLowerCase().includes("prototype");

    let specTransitional: number;
    let specSignature: number;
    let presoldTransitional: number;
    let presoldSignature: number;

    // Compute raw recorded counts from the scoped peer set. These are
    // used to determine which pathway applies and how much remaining
    // inventory still needs to be filled in via global ratios.
    let scopeRows = rows.filter(scopeFilter);
    if (!scopeRows.length) scopeRows = filtered;

    const scopeSpecs = scopeRows.filter(
      (r) =>
        (r.houseType === "Pending" || r.houseType === "Spec") &&
        r.contractDate != null
    );
    const scopePresolds = scopeRows.filter(
      (r) => r.houseType === "Pre-Sold" && r.contractDate != null
    );

    const rawSpecT    = scopeSpecs.filter((r)    => r.style === "Transitional").length;
    const rawSpecS    = scopeSpecs.filter((r)    => r.style === "Signature").length;
    const rawPresoldT = scopePresolds.filter((r) => r.style === "Transitional").length;
    const rawPresoldS = scopePresolds.filter((r) => r.style === "Signature").length;
    const rawTotal    = rawSpecT + rawSpecS + rawPresoldT + rawPresoldS;

    if (isPrototypeTarget || rawTotal === 0) {
      // Pathway 1 & 2: Prototype OR real neighborhood with no recorded sales.
      // Distribute the full cap using global style ratios.
      const buckets = distributeByGlobalRatios(cap);
      specTransitional    = buckets.specT;
      specSignature       = buckets.specS;
      presoldTransitional = buckets.presoldT;
      presoldSignature    = buckets.presoldS;
    } else if (rawTotal >= cap) {
      // Pathway 3a: Sufficient recorded data — use raw counts directly.
      specTransitional    = rawSpecT;
      specSignature       = rawSpecS;
      presoldTransitional = rawPresoldT;
      presoldSignature    = rawPresoldS;
    } else {
      // Pathway 3b: Partial recorded data — keep recorded counts as-is,
      // then proportionally fill the remainder using recorded style ratios
      // so the four buckets sum to availableLots.
      const remainder = cap - rawTotal;
      const extraSpecT    = Math.round((rawSpecT    / rawTotal) * remainder);
      const extraSpecS    = Math.round((rawSpecS    / rawTotal) * remainder);
      const extraPresoldT = Math.round((rawPresoldT / rawTotal) * remainder);
      const extraPresoldS = Math.max(
        0,
        remainder - extraSpecT - extraSpecS - extraPresoldT
      );

      specTransitional    = rawSpecT    + extraSpecT;
      specSignature       = rawSpecS    + extraSpecS;
      presoldTransitional = rawPresoldT + extraPresoldT;
      presoldSignature    = rawPresoldS + extraPresoldS;
    }

    const hs = {
      specStats: {
        // avgPrice and avgSqft use last-90-day sales only
        avgPrice: mean(soldSpecs90.filter((r) => r.price && r.price > 0).map((r) => r.price)),
        avgSqft:  mean(soldSpecs90.filter((r) => r.sqft  && r.sqft  > 0).map((r) => r.sqft)),
        popularPlan:  mode(soldSpecs90.map((r) => r.floorPlan)),
        transitional: specTransitional,
        signature:    specSignature,
      },
      presoldStats: {
        avgPrice: mean(soldPresolds90.filter((r) => r.price && r.price > 0).map((r) => r.price)),
        avgSqft:  mean(soldPresolds90.filter((r) => r.sqft  && r.sqft  > 0).map((r) => r.sqft)),
        popularPlan:  mode(soldPresolds90.map((r) => r.floorPlan)),
        transitional: presoldTransitional,
        signature:    presoldSignature,
      },
    };

    return { filteredRowsForNeighborhood: filtered, houseStats: hs };
  // cluster is included in deps to future-proof scoping ratios to cluster peers
  }, [rows, params, cluster]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Clean, stable derived params ----------
  const desiredTotalLots = useMemo(() => roundInt((params as any)?.availableLots), [params]);

  const neighborhoodTitleLower = useMemo(
    () => String((params as any)?.neighborhoodTitle ?? "").toLowerCase(),
    [params]
  );

  const neighborhoodIdLower = useMemo(
    () => String((params as any)?.neighborhoodID ?? "").toLowerCase(),
    [params]
  );

  const isPrototype = useMemo(() => {
    return (
      neighborhoodTitleLower.includes("prototype") ||
      neighborhoodIdLower.includes("prototype")
    );
  }, [neighborhoodTitleLower, neighborhoodIdLower]);

  // ---------- NEW: target remaining lots to satisfy invariant ----------
  // available_lots + future_lots = total_lots - unsold_specs - sold_specs - presolds
  const remainingLotsTarget = useMemo(() => {
    if (!summary) return 0;
    const unsold = roundInt(summary.current?.unsold_specs);
    const sold   = roundInt(summary.current?.sold_specs);
    const pres   = roundInt(summary.current?.presolds);
    const houses = unsold + sold + pres;
    return Math.max(0, desiredTotalLots - houses);
  }, [desiredTotalLots, summary]);

  // ---------- Available vs Future Lots from Property Type (base), scaled to remainingLotsTarget ----------
  const lotCounts = useMemo(() => {
    const AVAILABLE_SET = new Set(
      ["available lot", "lot hold", "future spec"].map((s) => s.toLowerCase())
    );
    const FUTURE_SET = new Set(
      [
        "will own",
        "option lot",
        "developing - controlled",
        "developing controlled",
        "developing - possible",
        "distant - controlled",
        "distant - possible",
      ].map((s) => s.toLowerCase())
    );

    if (isPrototype) {
      return {
        baseAvailableLots: 0,
        baseFutureLots: 0,
        baseTotalLots: 0,
        desiredTotalLots,
        remainingLotsTarget,
        adjustedAvailableLots: remainingLotsTarget,
        adjustedFutureLots: 0,
        isPrototype,
      };
    }

    if (!filteredRowsForNeighborhood || !filteredRowsForNeighborhood.length) {
      return {
        baseAvailableLots: 0,
        baseFutureLots: 0,
        baseTotalLots: 0,
        desiredTotalLots,
        remainingLotsTarget,
        adjustedAvailableLots: remainingLotsTarget,
        adjustedFutureLots: 0,
        isPrototype,
      };
    }

    let baseAvailableLots = 0;
    let baseFutureLots = 0;

    for (const r of filteredRowsForNeighborhood) {
      const pt = normKey((r as any).propertyType);
      if (!pt) continue;
      if (AVAILABLE_SET.has(pt)) baseAvailableLots += 1;
      if (FUTURE_SET.has(pt))    baseFutureLots    += 1;
    }

    const baseTotalLots = baseAvailableLots + baseFutureLots;

    if (baseTotalLots <= 0) {
      return {
        baseAvailableLots,
        baseFutureLots,
        baseTotalLots,
        desiredTotalLots,
        remainingLotsTarget,
        adjustedAvailableLots: remainingLotsTarget,
        adjustedFutureLots: 0,
        isPrototype,
      };
    }

    const availRatio = baseAvailableLots / baseTotalLots;
    const adjustedAvailableLots = Math.max(0, Math.round(remainingLotsTarget * availRatio));
    const adjustedFutureLots    = Math.max(0, remainingLotsTarget - adjustedAvailableLots);

    return {
      baseAvailableLots,
      baseFutureLots,
      baseTotalLots,
      desiredTotalLots,
      remainingLotsTarget,
      adjustedAvailableLots,
      adjustedFutureLots,
      isPrototype,
    };
  }, [filteredRowsForNeighborhood, desiredTotalLots, remainingLotsTarget, isPrototype]);

  // ---------- Sales / Absorption Stats from forecastData ----------
  //
  // Window semantics (anchored on the forecast timeline, NOT on first-sale-date):
  //
  //   startDate = data[0].dateObj           (earliest of Office Green Light / Contract Date)
  //   endDate   = data[last].dateObj        (final contract date in the forecast)
  //   today     = new Date()
  //
  //   Recorded Absorption    = sales(startDate → today)      / months(startDate → today)
  //                            — always populated
  //   Last 12 Months         = sales(today-12mo → today)     / 12
  //                            — null if recorded history < 12 months
  //   Last 6 Months          = sales(today-6mo → today)      / 6
  //                            — null if recorded history < 6 months
  //   Forecasted Absorption  = sales(today → endDate)        / months(today → endDate)
  //                            — always populated
  //   Lifetime Absorption    = sales(startDate → endDate)    / months(startDate → endDate)
  //                            — always populated
  //
  const salesStats = useMemo(() => {
    if (!forecastData || forecastData.length < 2) return null;

    const data = forecastData
      .map((d) => ({ ...d, dateObj: new Date(d.date) }))
      .sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());

    const today = new Date();
    const year  = today.getFullYear();

    const startDate = data[0].dateObj;
    const endDate   = data[data.length - 1].dateObj;

    // How much recorded history is available (start of data → today).
    // Used to gate the 6 / 12 month windows.
    const recordedMonthsAvailable = approxMonthsBetween(startDate, today);

    // ── Sales-counting helper ─────────────────────────────────────────────
    const countSalesBetween = (
      startExclusive: Date | null,
      endInclusive: Date | null
    ) => {
      let specSales = 0;
      let presoldSales = 0;
      for (let i = 1; i < data.length; i++) {
        const prev = data[i - 1];
        const curr = data[i];
        const dt   = curr.dateObj;
        if (startExclusive && dt <= startExclusive) continue;
        if (endInclusive   && dt  > endInclusive)   continue;
        specSales    += clampPositive(curr.sold_specs - prev.sold_specs);
        presoldSales += clampPositive(curr.presolds   - prev.presolds);
      }
      return { specSales, presoldSales };
    };

    // ── Current year (YTD recorded vs YTD projected) ────────────────────────
    const yearStart = new Date(year, 0, 1, 0, 0, 0, 0);
    const yearEnd   = new Date(year + 1, 0, 1, 0, 0, 0, 0);
    const ytd           = countSalesBetween(new Date(yearStart.getTime() - 1), today);
    const ytdForecasted = countSalesBetween(today, yearEnd);

    // ── Recorded absorption (startDate → today) — ALWAYS populated ──────────
    const recordedTotals = countSalesBetween(
      new Date(startDate.getTime() - 1),
      today
    );
    const recordedSpec    = safePerMonth(recordedTotals.specSales,    recordedMonthsAvailable);
    const recordedPresold = safePerMonth(recordedTotals.presoldSales, recordedMonthsAvailable);

    // ── Last 6 / 12 months — only if neighborhood has ≥ window of history ───
    const MIN_HISTORY_TOLERANCE = 0.5; // half-month slack for rounding

    let last6Spec: number | null = null;
    let last6Presold: number | null = null;
    if (recordedMonthsAvailable >= 6 - MIN_HISTORY_TOLERANCE) {
      const sixMoAgo = new Date(today);
      sixMoAgo.setMonth(sixMoAgo.getMonth() - 6);
      const t = countSalesBetween(sixMoAgo, today);
      const m = approxMonthsBetween(sixMoAgo, today);
      last6Spec    = safePerMonth(t.specSales,    m);
      last6Presold = safePerMonth(t.presoldSales, m);
    }

    let last12Spec: number | null = null;
    let last12Presold: number | null = null;
    if (recordedMonthsAvailable >= 12 - MIN_HISTORY_TOLERANCE) {
      const twelveMoAgo = new Date(today);
      twelveMoAgo.setMonth(twelveMoAgo.getMonth() - 12);
      const t = countSalesBetween(twelveMoAgo, today);
      const m = approxMonthsBetween(twelveMoAgo, today);
      last12Spec    = safePerMonth(t.specSales,    m);
      last12Presold = safePerMonth(t.presoldSales, m);
    }

    // ── Forecasted absorption (today → endDate) — ALWAYS populated ──────────
    const forecastTotals = countSalesBetween(today, endDate);
    const forecastMonths = approxMonthsBetween(today, endDate);
    const forecastedSpec    = safePerMonth(forecastTotals.specSales,    forecastMonths);
    const forecastedPresold = safePerMonth(forecastTotals.presoldSales, forecastMonths);

    // ── Lifetime absorption (startDate → endDate) — ALWAYS populated ────────
    const lifetimeTotals = countSalesBetween(
      new Date(startDate.getTime() - 1),
      endDate
    );
    const lifetimeMonths = approxMonthsBetween(startDate, endDate);
    const lifetimeSpec    = safePerMonth(lifetimeTotals.specSales,    lifetimeMonths);
    const lifetimePresold = safePerMonth(lifetimeTotals.presoldSales, lifetimeMonths);

    return {
      year,
      currentYearSales: ytd,
      forecastedCurrentYearSales: ytdForecasted,
      recordedMonthsAvailable,
      recordedAbsorptionPerMonth: {
        spec:    recordedSpec,
        presold: recordedPresold,
      },
      lifetimeAbsorptionPerMonth: {
        spec:    lifetimeSpec,
        presold: lifetimePresold,
      },
      last6MonthsPerMonth: {
        spec:    last6Spec,
        presold: last6Presold,
      },
      last12MonthsPerMonth: {
        spec:    last12Spec,
        presold: last12Presold,
      },
      forecastedAbsorptionPerMonth: {
        spec:    forecastedSpec,
        presold: forecastedPresold,
      },
    };
  }, [forecastData]);

  // ---------- Render ----------
  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between text-sm whitespace-nowrap w-full">
      <span className="text-left font-medium">{label}</span>
      <span className="text-right truncate">{value}</span>
    </div>
  );

  const formatIntOrDash = (n: number | null | undefined) =>
    typeof n === "number" && Number.isFinite(n) ? Math.round(n).toLocaleString() : "—";

  const formatPerMonthOrDash = (n: number | null | undefined) =>
    typeof n === "number" && Number.isFinite(n) ? n.toFixed(1) : "—";

  return (
    <div className={`amenity-output theme-${theme}`}>
      <Card
        style={{
          backgroundColor: "var(--card-bg)",
          color: "var(--text-color)",
          border: "1px solid var(--border-color)",
        }}
        className="rounded-xl shadow-md"
      >
        <CardHeader />
        <CardContent className="space-y-5" style={{ color: "var(--text-color)" }}>
          {errorText && (
            <Alert
              className="mt-2 rounded-md"
              style={{
                backgroundColor: "var(--error-bg)",
                color: "var(--error-text)",
                border: "1px solid var(--border-color)",
              }}
            >
              {errorText}
            </Alert>
          )}

          {/* --- Composition --- */}
          <div className="flex justify-center gap-10 mt-2">
            <div className="text-center">
              <span
                className="block text-2xl font-bold"
                style={{ color: "var(--accent-color)" }}
              >
                {spec != null ? `${spec}%` : "—"}
              </span>
              <span className="text-sm" style={{ color: "var(--muted-text)" }}>
                Spec
              </span>
            </div>
            <div className="text-center">
              <span
                className="block text-2xl font-bold"
                style={{ color: "var(--accent-color)" }}
              >
                {presold != null ? `${presold}%` : "—"}
              </span>
              <span className="text-sm" style={{ color: "var(--muted-text)" }}>
                Pre-Sold
              </span>
            </div>
          </div>

          {/* --- Summary Statistics --- */}
          {summary && (
            <div
              className="pt-4 mt-4 border-t space-y-3"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h2 className="text-lg font-bold text-center">Summary Statistics</h2>

              <Row label="Start Date:" value={formatLongDate(summary.startDate)} />
              <Row label="End Date:"   value={formatLongDate(summary.endDate)}   />
              <Row
                label="Total Duration:"
                value={`${summary.totalYears.toFixed(2)} Years`}
              />

              <div className="pt-2 space-y-1">
                <span className="font-semibold block">Inventory:</span>

                <Row label="Total Lots:"     value={formatIntOrDash(lotCounts.desiredTotalLots)}      />
                <Row label="Available Lots:" value={formatIntOrDash(lotCounts.adjustedAvailableLots)} />
                <Row label="Future Lots:"    value={formatIntOrDash(lotCounts.adjustedFutureLots)}    />
                <Row label="Unsold Specs:"   value={summary.current.unsold_specs} />
                <Row label="Sold Specs:"     value={summary.current.sold_specs}   />
                <Row label="Pre-Solds:"      value={summary.current.presolds}     />
              </div>

              <div className="pt-2 space-y-1">
                <span className="font-semibold block">Forecasted Sales:</span>
                <Row label="Sold Specs:" value={summary.last.sold_specs} />
                <Row label="Pre-Solds:"  value={summary.last.presolds}   />
              </div>
            </div>
          )}

          {/* --- House Sales Statistics --- */}
          {houseStats && summary && (
            <div
              className="pt-4 mt-4 border-t space-y-4"
              style={{ borderColor: "var(--border-color)" }}
            >
              <h2 className="text-lg font-bold text-center">House Sales Statistics</h2>

              <div className="grid grid-cols-4 text-sm gap-y-2">
                <span></span>
                <span className="font-semibold text-center">Spec</span>
                <span className="font-semibold text-center">Pre-Sold</span>
                <span className="font-semibold text-center">Total</span>

                {salesStats && (
                  <>
                    <span className="font-medium">{salesStats.year} Recorded:</span>
                    <span className="text-center">
                      {formatIntOrDash(salesStats.currentYearSales.specSales)}
                    </span>
                    <span className="text-center">
                      {formatIntOrDash(salesStats.currentYearSales.presoldSales)}
                    </span>
                    <span className="text-center">
                      {formatIntOrDash(
                        (salesStats.currentYearSales.specSales ?? 0) +
                          (salesStats.currentYearSales.presoldSales ?? 0)
                      )}
                    </span>

                    <span className="font-medium">{salesStats.year} Projected:</span>
                    <span className="text-center">
                      {formatIntOrDash(salesStats.forecastedCurrentYearSales.specSales)}
                    </span>
                    <span className="text-center">
                      {formatIntOrDash(salesStats.forecastedCurrentYearSales.presoldSales)}
                    </span>
                    <span className="text-center">
                      {formatIntOrDash(
                        (salesStats.forecastedCurrentYearSales.specSales ?? 0) +
                          (salesStats.forecastedCurrentYearSales.presoldSales ?? 0)
                      )}
                    </span>

                    {/* 1. Recorded Absorption — startDate → today */}
                    <span className="font-medium">Recorded Absorption:</span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.recordedAbsorptionPerMonth.spec)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.recordedAbsorptionPerMonth.presold)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(
                        sumOrNull(
                          salesStats.recordedAbsorptionPerMonth.spec,
                          salesStats.recordedAbsorptionPerMonth.presold
                        )
                      )}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>

                    {/* 2. Last 12 Months — null if < 12 months of history */}
                    <span className="font-medium">Last 12 Months:</span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.last12MonthsPerMonth.spec)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.last12MonthsPerMonth.presold)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(
                        sumOrNull(
                          salesStats.last12MonthsPerMonth.spec,
                          salesStats.last12MonthsPerMonth.presold
                        )
                      )}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>

                    {/* 3. Last 6 Months — null if < 6 months of history */}
                    <span className="font-medium">Last 6 Months:</span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.last6MonthsPerMonth.spec)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.last6MonthsPerMonth.presold)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(
                        sumOrNull(
                          salesStats.last6MonthsPerMonth.spec,
                          salesStats.last6MonthsPerMonth.presold
                        )
                      )}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>

                    {/* 4. Forecasted Absorption — today → endDate */}
                    <span className="font-medium">Forecasted Absorption:</span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.forecastedAbsorptionPerMonth.spec)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.forecastedAbsorptionPerMonth.presold)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(
                        sumOrNull(
                          salesStats.forecastedAbsorptionPerMonth.spec,
                          salesStats.forecastedAbsorptionPerMonth.presold
                        )
                      )}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>

                    {/* 5. Lifetime Absorption — startDate → endDate */}
                    <span className="font-medium">Lifetime Absorption:</span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.lifetimeAbsorptionPerMonth.spec)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(salesStats.lifetimeAbsorptionPerMonth.presold)}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                    <span className="text-center">
                      {formatPerMonthOrDash(
                        sumOrNull(
                          salesStats.lifetimeAbsorptionPerMonth.spec,
                          salesStats.lifetimeAbsorptionPerMonth.presold
                        )
                      )}{" "}
                      <span className="opacity-80">/ mo</span>
                    </span>
                  </>
                )}

                <span>Average Price:</span>
                <span className="text-center">
                  {houseStats.specStats.avgPrice
                    ? `$${houseStats.specStats.avgPrice.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}`
                    : "—"}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.avgPrice
                    ? `$${houseStats.presoldStats.avgPrice.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}`
                    : "—"}
                </span>
                <span className="text-center">
                  {formatCurrencyOrDash(
                    avgOrNull(
                      houseStats.specStats.avgPrice,
                      houseStats.presoldStats.avgPrice
                    )
                  )}
                </span>

                <span>Average Size:</span>
                <span className="text-center">
                  {houseStats.specStats.avgSqft
                    ? `${houseStats.specStats.avgSqft.toFixed(0)} sqft`
                    : "—"}
                </span>
                <span className="text-center">
                  {houseStats.presoldStats.avgSqft
                    ? `${houseStats.presoldStats.avgSqft.toFixed(0)} sqft`
                    : "—"}
                </span>
                <span className="text-center">
                  {avgOrNull(
                    houseStats.specStats.avgSqft,
                    houseStats.presoldStats.avgSqft
                  ) != null
                    ? `${avgOrNull(
                        houseStats.specStats.avgSqft,
                        houseStats.presoldStats.avgSqft
                      )!.toFixed(0)} sqft`
                    : "—"}
                </span>

                <span>Transitional:</span>
                <span className="text-center">{houseStats.specStats.transitional}</span>
                <span className="text-center">{houseStats.presoldStats.transitional}</span>
                <span className="text-center">
                  {(houseStats.specStats.transitional ?? 0) +
                    (houseStats.presoldStats.transitional ?? 0)}
                </span>

                <span>Signature:</span>
                <span className="text-center">{houseStats.specStats.signature}</span>
                <span className="text-center">{houseStats.presoldStats.signature}</span>
                <span className="text-center">
                  {(houseStats.specStats.signature ?? 0) +
                    (houseStats.presoldStats.signature ?? 0)}
                </span>
              </div>

              {(summary.recordedPeakQuarter || summary.forecastedPeakQuarter) && (
                <div
                  className="pt-4 mt-4 border-t space-y-3"
                  style={{ borderColor: "var(--border-color)" }}
                >
                  <h2 className="text-lg font-bold text-center">Peak Demand</h2>

                  <div className="grid grid-cols-3 text-sm gap-y-2">
                    <span></span>
                    <span className="font-semibold text-center">Recorded</span>
                    <span className="font-semibold text-center">Forecasted</span>

                    <span>Quarter:</span>
                    <span className="text-center">
                      {summary.recordedPeakQuarter
                        ? `${summary.recordedPeakQuarter.key.split("-")[1]} ${
                            summary.recordedPeakQuarter.key.split("-")[0]
                          }`
                        : "—"}
                    </span>
                    <span className="text-center">
                      {summary.forecastedPeakQuarter
                        ? `${summary.forecastedPeakQuarter.key.split("-")[1]} ${
                            summary.forecastedPeakQuarter.key.split("-")[0]
                          }`
                        : "—"}
                    </span>

                    <span className="font-medium">Sales:</span>

                    <span className="text-center leading-tight">
                      {summary.recordedPeakQuarter ? (
                        <>
                          <div>{summary.recordedPeakQuarter.specs} Specs</div>
                          <div>{summary.recordedPeakQuarter.presolds} Pre-Solds</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </span>

                    <span className="text-center leading-tight">
                      {summary.forecastedPeakQuarter ? (
                        <>
                          <div>{summary.forecastedPeakQuarter.specs} Specs</div>
                          <div>{summary.forecastedPeakQuarter.presolds} Pre-Solds</div>
                        </>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AmenityOutput;
